import {
  BufferGeometry,
  Color,
  ConeGeometry,
  CylinderGeometry,
  DoubleSide,
  ExtrudeGeometry,
  Float32BufferAttribute,
  Fog,
  Group,
  Mesh,
  PlaneGeometry,
  Shape,
  SphereGeometry,
  Vector2,
  Vector3,
} from "three";
import { getGeoOrigin, groundElevation, lonLatToLocal, ringCentroid } from "./geo";
import { createTree, hash01 } from "./props/createTree";
import { palette, toonMat, withOutline } from "./style/toon";

export type DistrictMeta = {
  attribution: string;
  bbox: { south: number; west: number; north: number; east: number };
};

export type LoadDistrictOptions = {
  baseUrl?: string;
  /** Skip OSM footprints near owned hero props */
  heroExclusions?: Array<{ lat: number; lon: number; r: number }>;
  /** Extra name match to skip (e.g. /frontenac/i) */
  skipBuildingName?: RegExp;
  /** Skip St. Lawrence-style water plane */
  skipWater?: boolean;
  /** Multiply park tree count (La Fortuna denser canopy) */
  treeDensity?: number;
  /** Prefer terracotta roofs over copper/slate */
  terracottaRoofs?: boolean;
  /** Place simple lamp posts around park rings */
  parkLamps?: boolean;
  /** Scale road ribbon width (La Fortuna reads better wider) */
  roadWidthScale?: number;
  /** Lift roads above terrain/parks to avoid z-fighting */
  roadLift?: number;
};

type Fc = {
  type: "FeatureCollection";
  features: Array<{
    type: "Feature";
    properties: Record<string, unknown>;
    geometry: {
      type: string;
      coordinates: number[][] | number[][][];
    };
  }>;
};

const HEIGHT_EXAGGERATION = 1.12;

type DistrictMats = {
  park: ReturnType<typeof toonMat>;
  road: ReturnType<typeof toonMat>;
  path: ReturnType<typeof toonMat>;
  wall: ReturnType<typeof toonMat>;
  buildingResidential: ReturnType<typeof toonMat>;
  buildingCommercial: ReturnType<typeof toonMat>;
  buildingPublic: ReturnType<typeof toonMat>;
  buildingCivic: ReturnType<typeof toonMat>;
  buildingUtility: ReturnType<typeof toonMat>;
  buildingTall: ReturnType<typeof toonMat>;
  roofSlate: ReturnType<typeof toonMat>;
  roofCopper: ReturnType<typeof toonMat>;
  roofFlat: ReturnType<typeof toonMat>;
  hero: ReturnType<typeof toonMat>;
  water: ReturnType<typeof toonMat>;
  lamp: ReturnType<typeof toonMat>;
};

/** Materials read current palette — call after applyTropicalPalette if needed. */
function makeMats(): DistrictMats {
  const water = toonMat(palette.water, { transparent: true, opacity: 0.88 });
  water.side = DoubleSide;
  const road = toonMat(palette.road);
  road.side = DoubleSide;
  road.polygonOffset = true;
  road.polygonOffsetFactor = -4;
  road.polygonOffsetUnits = -4;
  const path = toonMat(0xc4b8a0);
  path.side = DoubleSide;
  path.polygonOffset = true;
  path.polygonOffsetFactor = -3;
  path.polygonOffsetUnits = -3;
  return {
    park: toonMat(palette.park),
    road,
    path,
    wall: toonMat(palette.wall),
    buildingResidential: toonMat(palette.buildingResidential),
    buildingCommercial: toonMat(palette.buildingCommercial),
    buildingPublic: toonMat(palette.buildingPublic),
    buildingCivic: toonMat(palette.buildingCivic),
    buildingUtility: toonMat(palette.buildingUtility),
    buildingTall: toonMat(0xcfc4b4),
    roofSlate: toonMat(palette.roofSlate),
    roofCopper: toonMat(palette.roofCopper),
    roofFlat: toonMat(palette.roofFlat),
    hero: toonMat(palette.stone),
    water,
    lamp: toonMat(0x3a3836),
  };
}

let mats = makeMats();
let loadOpts: LoadDistrictOptions = {};

type RoofKind = "slate" | "copper" | "flat";
type FacadeKind =
  | "residential"
  | "commercial"
  | "public"
  | "civic"
  | "utility"
  | "hero";

function facadeKindFor(building: unknown, name: unknown, hero: boolean): FacadeKind {
  if (hero) return "hero";
  const t = String(building || "").toLowerCase();
  if (t.includes("église") || t.includes("eglise") || t.includes("école") || t.includes("hopital") || t.includes("hôpital")) {
    return "civic";
  }
  if (t.includes("public") || t.includes("municipal")) return "public";
  if (t.includes("commercial") || t.includes("hôtel") || t.includes("hotel")) {
    return "commercial";
  }
  if (t.includes("garage") || t.includes("industriel") || t.includes("annexe") || t.includes("indéfini")) {
    return "utility";
  }
  void name;
  return "residential";
}

function facadeMat(kind: FacadeKind, heightM: number) {
  if (kind === "hero") return mats.hero;
  if (kind === "civic") return mats.buildingCivic;
  if (kind === "public") return mats.buildingPublic;
  if (kind === "commercial") return mats.buildingCommercial;
  if (kind === "utility") return mats.buildingUtility;
  if (heightM >= 18) return mats.buildingTall;
  return mats.buildingResidential;
}

function roofKindFor(building: unknown, name: unknown): RoofKind {
  if (loadOpts.terracottaRoofs) {
    const t = String(building || "").toLowerCase();
    if (t.includes("garage") || t.includes("industriel") || t.includes("annexe")) {
      return "flat";
    }
    return "copper"; // copper slot remapped to terracotta via mats.roofCopper
  }
  const t = String(building || "").toLowerCase();
  const n = String(name || "").toLowerCase();
  if (
    t.includes("église") ||
    t.includes("eglise") ||
    t.includes("church") ||
    n.includes("notre-dame") ||
    n.includes("basilique") ||
    n.includes("cathedral") ||
    n.includes("frontenac")
  ) {
    return "copper";
  }
  if (
    t.includes("public") ||
    t.includes("municipal") ||
    t.includes("commercial") ||
    t.includes("hôtel") ||
    t.includes("hotel")
  ) {
    return "copper";
  }
  if (t.includes("garage") || t.includes("industriel") || t.includes("annexe")) {
    return "flat";
  }
  return "slate";
}

function roofMat(kind: RoofKind) {
  if (kind === "copper") return mats.roofCopper;
  if (kind === "flat") return mats.roofFlat;
  return mats.roofSlate;
}

function ringToShape(ring: number[][]): Shape | null {
  if (ring.length < 3) return null;
  const pts: Vector2[] = [];
  for (const [lon, lat] of ring) {
    const p = lonLatToLocal(lon, lat, 0);
    pts.push(new Vector2(p.x, -(p.z)));
  }
  const shape = new Shape();
  shape.moveTo(pts[0].x, pts[0].y);
  for (let i = 1; i < pts.length; i++) shape.lineTo(pts[i].x, pts[i].y);
  shape.closePath();
  return shape;
}

function extrudeUp(shape: Shape, height: number): ExtrudeGeometry {
  const geom = new ExtrudeGeometry(shape, {
    depth: height,
    bevelEnabled: height > 8,
    bevelThickness: 0.18,
    bevelSize: 0.22,
    bevelSegments: 1,
  });
  geom.rotateX(-Math.PI / 2);
  return geom;
}

function addFlatPolygon(
  group: Group,
  ring: number[][],
  material: ReturnType<typeof toonMat>,
  thickness = 0.25,
) {
  const shape = ringToShape(ring);
  if (!shape) return;
  const { lon, lat } = ringCentroid(ring);
  const elev = groundElevation(lat, lon);
  const mesh = new Mesh(extrudeUp(shape, thickness), material);
  mesh.position.y = elev + 0.12;
  group.add(mesh);
}

function addBuilding(
  group: Group,
  ring: number[][],
  heightM: number,
  hero: boolean,
  _accent: boolean,
  buildingType: unknown,
  name: unknown,
) {
  const shape = ringToShape(ring);
  if (!shape) return;
  const { lon, lat } = ringCentroid(ring);
  const elev = groundElevation(lat, lon);
  const h = Math.max(4, heightM * HEIGHT_EXAGGERATION * (hero ? 1.35 : 1));
  const facade = facadeKindFor(buildingType, name, hero);
  const bodyMat = facadeMat(facade, heightM);
  const mesh = withOutline(new Mesh(extrudeUp(shape, h), bodyMat), 1.025);
  mesh.position.y = elev;
  mesh.userData.kind = "building";
  mesh.userData.facade = facade;
  mesh.userData.localX = lonLatToLocal(lon, lat, 0).x;
  mesh.userData.localZ = lonLatToLocal(lon, lat, 0).z;
  group.add(mesh);

  const kind = roofKindFor(buildingType, name);
  const roofShape = ringToShape(ring);
  if (!roofShape) return;
  const roofH = kind === "flat" ? 0.4 : kind === "copper" ? 1.1 : 0.7;
  const roof = new Mesh(extrudeUp(roofShape, roofH), roofMat(kind));
  roof.position.y = elev + h;
  group.add(roof);

  // Church / copper roofs get a small steeple cue for recognizability
  if (kind === "copper" && (hero || String(buildingType).includes("Église"))) {
    const steeple = withOutline(
      new Mesh(new ConeGeometry(1.8, 6, 8), mats.roofCopper),
      1.05,
    );
    const local = lonLatToLocal(lon, lat, 0);
    steeple.position.set(local.x, elev + h + 4, local.z);
    group.add(steeple);
  }
}

/**
 * Resample an OSM lon/lat line every `stepM` meters so ribbons follow soft DEM.
 */
function densifyLonLat(coords: number[][], stepM = 3.5): number[][] {
  if (coords.length < 2) return coords;
  const out: number[][] = [[coords[0][0], coords[0][1]]];
  for (let i = 0; i < coords.length - 1; i++) {
    const [lon0, lat0] = coords[i];
    const [lon1, lat1] = coords[i + 1];
    const a = lonLatToLocal(lon0, lat0, 0);
    const b = lonLatToLocal(lon1, lat1, 0);
    const len = Math.hypot(b.x - a.x, b.z - a.z);
    const n = Math.max(1, Math.ceil(len / stepM));
    for (let k = 1; k <= n; k++) {
      const t = k / n;
      out.push([lon0 + (lon1 - lon0) * t, lat0 + (lat1 - lat0) * t]);
    }
  }
  return out;
}

/**
 * Continuous asphalt/path strip along an OSM LineString (no per-segment gaps).
 * Densified + per-vertex ground sample so ribbons don't sink into soft DEM.
 */
function addRoadRibbon(
  group: Group,
  coords: number[][],
  width = 3.2,
  kind: "road" | "path" = "road",
) {
  const baseLift = loadOpts.roadLift ?? 0.35;
  const lift = kind === "path" ? baseLift + 0.35 : baseLift;
  const scale = loadOpts.roadWidthScale ?? 1;
  const half = (width * scale) * 0.5;
  const step = kind === "path" ? 2.5 : 3.5;

  const densified = densifyLonLat(coords, step);
  const cleaned: Vector3[] = [];
  for (const [lon, lat] of densified) {
    const p = lonLatToLocal(lon, lat, 0);
    if (
      cleaned.length === 0 ||
      cleaned[cleaned.length - 1].distanceTo(p) >= 0.25
    ) {
      cleaned.push(p);
    }
  }
  if (cleaned.length < 2) return;

  const left: Vector3[] = [];
  const right: Vector3[] = [];
  for (let i = 0; i < cleaned.length; i++) {
    const prev = cleaned[Math.max(0, i - 1)];
    const next = cleaned[Math.min(cleaned.length - 1, i + 1)];
    let dx = next.x - prev.x;
    let dz = next.z - prev.z;
    const len = Math.hypot(dx, dz) || 1;
    dx /= len;
    dz /= len;
    const nx = -dz;
    const nz = dx;
    // Re-sample elev at the offset curb points so banks don't clip hillsides
    const y = cleaned[i].y + lift;
    left.push(
      new Vector3(cleaned[i].x + nx * half, y, cleaned[i].z + nz * half),
    );
    right.push(
      new Vector3(cleaned[i].x - nx * half, y, cleaned[i].z - nz * half),
    );
  }

  // Lift curb vertices to local ground under each curb (handles cross-slope)
  for (let i = 0; i < cleaned.length; i++) {
    const origin = getGeoOrigin();
    const mPerLon = 111_320 * Math.cos((origin.lat * Math.PI) / 180);
    for (const side of [left[i], right[i]]) {
      const lat = origin.lat + side.z / 111_320;
      const lon = origin.lon - side.x / mPerLon;
      side.y = groundElevation(lat, lon) + lift;
    }
  }

  const positions: number[] = [];
  const indices: number[] = [];
  for (let i = 0; i < cleaned.length; i++) {
    positions.push(left[i].x, left[i].y, left[i].z);
    positions.push(right[i].x, right[i].y, right[i].z);
  }
  for (let i = 0; i < cleaned.length - 1; i++) {
    const a = i * 2;
    const b = a + 1;
    const c = a + 2;
    const d = a + 3;
    indices.push(a, c, b, b, c, d);
  }

  const geom = new BufferGeometry();
  geom.setAttribute("position", new Float32BufferAttribute(positions, 3));
  geom.setIndex(indices);
  geom.computeVertexNormals();

  const mesh = new Mesh(geom, kind === "path" ? mats.path : mats.road);
  mesh.renderOrder = kind === "path" ? 2 : 1;
  mesh.userData.kind = kind;
  group.add(mesh);
}

/** Pull park polygon slightly inward so curb streets stay visible. */
function insetRing(ring: number[][], meters = 3.5): number[][] {
  const { lon, lat } = ringCentroid(ring);
  const mLon = meters / (111_320 * Math.cos((lat * Math.PI) / 180));
  const mLat = meters / 111_320;
  return ring.map(([x, y]) => {
    const dx = x - lon;
    const dy = y - lat;
    const dist = Math.hypot(dx / mLon, dy / mLat);
    if (dist < 1e-6) return [x, y];
    const s = Math.max(0.15, 1 - 1 / dist);
    return [lon + dx * s, lat + dy * s];
  });
}

function addWallRibbon(group: Group, coords: number[][]) {
  for (let i = 0; i < coords.length - 1; i++) {
    const a = lonLatToLocal(coords[i][0], coords[i][1], 0);
    const b = lonLatToLocal(coords[i + 1][0], coords[i + 1][1], 0);
    const dx = b.x - a.x;
    const dz = b.z - a.z;
    const len = Math.hypot(dx, dz);
    if (len < 0.5) continue;
    const elev = (a.y + b.y) / 2;
    const geom = new PlaneGeometry(len, 7);
    const mesh = withOutline(new Mesh(geom, mats.wall), 1.04);
    mesh.position.set((a.x + b.x) / 2, elev + 3.5, (a.z + b.z) / 2);
    mesh.rotation.y = Math.atan2(dx, dz);
    group.add(mesh);
  }
}

function addTreesInPark(group: Group, ring: number[][], parkIndex: number) {
  const { lon, lat } = ringCentroid(ring);
  let minLon = Infinity;
  let maxLon = -Infinity;
  let minLat = Infinity;
  let maxLat = -Infinity;
  for (const [x, y] of ring) {
    minLon = Math.min(minLon, x);
    maxLon = Math.max(maxLon, x);
    minLat = Math.min(minLat, y);
    maxLat = Math.max(maxLat, y);
  }
  const density = loadOpts.treeDensity ?? 1;
  const count = Math.round((3 + Math.floor(hash01(parkIndex, 3) * 5)) * density);
  for (let i = 0; i < count; i++) {
    const u = hash01(parkIndex, i * 2 + 1);
    const v = hash01(parkIndex, i * 2 + 2);
    const tLon = minLon + (maxLon - minLon) * (0.2 + u * 0.6);
    const tLat = minLat + (maxLat - minLat) * (0.2 + v * 0.6);
    const lon2 = lon * 0.35 + tLon * 0.65;
    const lat2 = lat * 0.35 + tLat * 0.65;
    const tree = createTree(parkIndex * 17 + i);
    const pos = lonLatToLocal(lon2, lat2, 0);
    tree.position.copy(pos);
    tree.userData.localX = pos.x;
    tree.userData.localZ = pos.z;
    tree.rotation.y = u * Math.PI * 2;
    if (density > 1.2) tree.scale.setScalar(1.05 + hash01(parkIndex, i) * 0.35);
    group.add(tree);
  }

  if (loadOpts.parkLamps) {
    const lampCount = 6;
    for (let i = 0; i < lampCount; i++) {
      const t = (i + 0.5) / lampCount;
      // Sample ring edge
      const idx = Math.min(ring.length - 2, Math.floor(t * (ring.length - 1)));
      const [lonL, latL] = ring[idx];
      const lamp = createLampPost();
      const pos = lonLatToLocal(lonL, latL, 0);
      lamp.position.copy(pos);
      group.add(lamp);
    }
  }
}

function createLampPost(): Group {
  const g = new Group();
  g.name = "lamp";
  const pole = withOutline(
    new Mesh(new CylinderGeometry(0.08, 0.1, 3.4, 6), mats.lamp),
    1.06,
  );
  pole.position.y = 1.7;
  g.add(pole);
  const head = withOutline(
    new Mesh(new SphereGeometry(0.22, 8, 6), toonMat(0xf5e6c8)),
    1.08,
  );
  head.position.y = 3.45;
  g.add(head);
  return g;
}

function addTerrain(group: Group, bbox: DistrictMeta["bbox"]) {
  const origin = getGeoOrigin();
  const segs = 128;
  const sw = lonLatToLocal(bbox.west, bbox.south, 0);
  const ne = lonLatToLocal(bbox.east, bbox.north, 0);
  const width = Math.abs(ne.x - sw.x) + 40;
  const depth = Math.abs(ne.z - sw.z) + 40;
  const cx = (sw.x + ne.x) / 2;
  const cz = (sw.z + ne.z) / 2;

  const geom = new PlaneGeometry(width, depth, segs, segs);
  geom.rotateX(-Math.PI / 2);
  const pos = geom.attributes.position;
  const colors = new Float32Array(pos.count * 3);
  const cLow = new Color(palette.groundLow);
  const cMid = new Color(palette.groundMid);
  const cHigh = new Color(palette.groundHigh);
  const cCliff = new Color(palette.cliff);
  const tmp = new Color();
  const mPerLon = 111_320 * Math.cos((origin.lat * Math.PI) / 180);

  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i) + cx;
    const z = pos.getZ(i) + cz;
    const dLon = -x / mPerLon;
    const dLat = z / 111_320;
    const lat = origin.lat + dLat;
    const lon = origin.lon + dLon;
    const elev = groundElevation(lat, lon);
    pos.setY(i, elev);

    const span = loadOpts.terracottaRoofs ? 28 : 95;
    const base = loadOpts.terracottaRoofs ? 3 : 5;
    const t = Math.min(1, Math.max(0, (elev - base) / span));
    if (t < 0.22) tmp.copy(cLow);
    else if (t < 0.45) tmp.lerpColors(cLow, cMid, (t - 0.22) / 0.23);
    else if (t < 0.7) tmp.lerpColors(cMid, cHigh, (t - 0.45) / 0.25);
    else tmp.lerpColors(cHigh, cCliff, (t - 0.7) / 0.3);
    colors[i * 3] = tmp.r;
    colors[i * 3 + 1] = tmp.g;
    colors[i * 3 + 2] = tmp.b;
  }
  pos.needsUpdate = true;
  geom.setAttribute("color", new Float32BufferAttribute(colors, 3));
  geom.computeVertexNormals();

  const mat = toonMat(0xffffff);
  mat.vertexColors = true;
  mat.side = DoubleSide;
  const mesh = new Mesh(geom, mat);
  mesh.position.set(cx, 0, cz);
  mesh.name = "terrain-stylized";
  group.add(mesh);

  if (!loadOpts.skipWater) {
    const water = new Mesh(new PlaneGeometry(220, 160), mats.water);
    const wPos = lonLatToLocal(-71.2008, 46.8124, 0);
    water.rotation.x = -Math.PI / 2;
    water.position.set(wPos.x - 40, Math.max(0.5, wPos.y - 0.8), wPos.z);
    group.add(water);
  }
}

function isHeroName(name: unknown): boolean {
  if (typeof name !== "string") return false;
  const n = name.toLowerCase();
  return (
    n.includes("notre-dame") ||
    n.includes("basilique") ||
    n.includes("cathedral") ||
    n.includes("royal") ||
    n.includes("san juan bosco")
  );
}

const DEFAULT_HERO_EXCLUSIONS: Array<{ lat: number; lon: number; r: number }> = [
  { lat: 46.81196, lon: -71.20536, r: 84 },
  { lat: 46.80955, lon: -71.21205, r: 35 },
  { lat: 46.81255, lon: -71.20435, r: 40 },
];

function nearOwnedHero(lon: number, lat: number): boolean {
  const list = loadOpts.heroExclusions ?? DEFAULT_HERO_EXCLUSIONS;
  for (const h of list) {
    const meters = Math.hypot(
      (lat - h.lat) * 111_320,
      (lon - h.lon) * 111_320 * Math.cos((lat * Math.PI) / 180),
    );
    if (meters < h.r) return true;
  }
  return false;
}

export async function loadDistrict(
  baseUrlOrOpts: string | LoadDistrictOptions = "/district/old-quebec",
): Promise<{ group: Group; meta: DistrictMeta }> {
  const opts: LoadDistrictOptions =
    typeof baseUrlOrOpts === "string"
      ? { baseUrl: baseUrlOrOpts }
      : baseUrlOrOpts;
  loadOpts = opts;
  mats = makeMats();
  const baseUrl = opts.baseUrl ?? "/district/old-quebec";
  const skipName = opts.skipBuildingName ?? /frontenac/i;

  const [meta, buildings, roads, parks, walls] = await Promise.all([
    fetch(`${baseUrl}/meta.json`).then((r) => r.json()) as Promise<DistrictMeta>,
    fetch(`${baseUrl}/buildings.geojson`).then((r) => r.json()) as Promise<Fc>,
    fetch(`${baseUrl}/roads.geojson`).then((r) => r.json()) as Promise<Fc>,
    fetch(`${baseUrl}/parks.geojson`).then((r) => r.json()) as Promise<Fc>,
    fetch(`${baseUrl}/walls.geojson`).then((r) => r.json()) as Promise<Fc>,
  ]);

  const group = new Group();
  group.name = "district";

  addTerrain(group, meta.bbox);

  let parkIndex = 0;
  for (const f of parks.features) {
    if (f.geometry.type !== "Polygon") continue;
    const ring = f.geometry.coordinates[0] as number[][];
    // Inset so framing streets (C. 470, Av. 331, Vía 142) stay on asphalt, not under grass
    const grass = loadOpts.terracottaRoofs ? insetRing(ring, 4.5) : ring;
    addFlatPolygon(group, grass, mats.park, 0.12);
    addTreesInPark(group, grass, parkIndex++);
  }

  for (const f of roads.features) {
    if (f.geometry.type !== "LineString") continue;
    const hw = String(f.properties.highway || "");
    const isPath =
      hw === "pedestrian" || hw === "footway" || hw === "path" || hw === "steps";
    const width = isPath
      ? 2.8
      : hw === "service"
        ? 4.0
        : hw === "residential"
          ? 6.5
          : hw === "tertiary"
            ? 8.0
            : hw === "secondary" || hw === "primary"
              ? 10.5
              : 6.5;
    addRoadRibbon(
      group,
      f.geometry.coordinates as number[][],
      width,
      isPath ? "path" : "road",
    );
  }

  for (const f of walls.features) {
    if (f.geometry.type !== "LineString") continue;
    addWallRibbon(group, f.geometry.coordinates as number[][]);
  }

  let buildingCount = 0;
  let skippedHero = 0;
  let i = 0;
  for (const f of buildings.features) {
    if (f.geometry.type !== "Polygon") continue;
    const ring = f.geometry.coordinates[0] as number[][];
    const { lon, lat } = ringCentroid(ring);
    const name = f.properties.name;
    if (
      nearOwnedHero(lon, lat) ||
      (typeof name === "string" && skipName.test(name))
    ) {
      skippedHero++;
      continue;
    }
    const height = Number(f.properties.height) || 10;
    addBuilding(
      group,
      ring,
      height,
      isHeroName(name),
      i % 7 === 0,
      f.properties.building,
      name,
    );
    buildingCount++;
    i++;
  }

  console.info(
    `[PlaceStage] stylized twin: ${buildingCount} buildings, ${parkIndex} parks with trees (skipped ${skippedHero} heroes)`,
  );
  return { group, meta };
}

export function applyStageAtmosphere(
  scene: {
    background: Color;
    fog: Fog | null;
  },
  fogNear = 180,
  fogFar = 980,
) {
  scene.background = new Color(palette.sky);
  scene.fog = new Fog(palette.fog, fogNear, fogFar);
}
